import React, { useState, useEffect } from 'react';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';

// Per-dispatch-card copy (different heading per card, matching the pasted code).
const CONTEXTS = {
  'Korea Keynote': {
    badge: 'KEYNOTE DISPATCH · SEOUL',
    title: 'Request Keynote Monograph',
    desc: 'Full transcript and sovereign AI strategic address delivered to the Korea Defense & Security Forum (KRINS).',
    btn: 'Transmit Keynote Dossier',
    successTitle: 'Keynote Dossier Dispatched',
    successDesc: 'The KRINS keynote address and strategic transcript has been routed to your institutional email.',
  },
  'IdeaSpring Capital Case Brief': {
    badge: 'PORTFOLIO DISPATCH · DEEP-TECH VC',
    title: 'Request Confidential Case Brief',
    desc: 'Autonomous PortCo monitoring and strategic exit positioning dossier prepared under standard mutual confidentiality.',
    btn: 'Transmit Portfolio Case Brief',
    successTitle: 'Case Brief Dispatched',
    successDesc: 'The IdeaSpring Capital deep-tech intelligence brief has been securely routed to your verified email.',
  },
  'Executive AI Masterclass Brief': {
    badge: 'EXECUTIVE LAB · WORKFLOW ARCHITECTURE',
    title: 'Request Masterclass Dossier',
    desc: 'Complete orchestration curriculum and workflow architecture schema (Screening, Diligence, Exit) for investment committees.',
    btn: 'Transmit Masterclass Curriculum',
    successTitle: 'Curriculum Dispatched',
    successDesc: 'The executive dealmaking workflow schemas and foundation model benchmarks have been routed to your email.',
  },
  'European Strategy Monograph': {
    badge: 'STRATEGY DISPATCH · LUXEMBOURG',
    title: 'Request M&A Diligence Monograph',
    desc: 'Dr. Bharat Madan’s analytical framework for de-risking cross-border Western European acquisitions in 72 hours.',
    btn: 'Transmit Diligence Monograph',
    successTitle: 'Monograph Dispatched',
    successDesc: 'The cross-border European M&A monograph has been transmitted directly to your institutional email.',
  },
};

const fallback = {
  badge: 'LEADERSHIP DIALOGUE · DIRECT INTAKE',
  title: 'Connect with Leadership',
  desc: 'Direct confidential intake with Founding Partners across Abu Dhabi, New York, and Luxembourg.',
  btn: 'Transmit to Founding Partners',
  successTitle: 'Transmitted to Leadership',
  successDesc: 'Your brief has been forwarded directly to Balbinder Singh Bhatia (CEO) and Maheshwar Damojipurapu (COO). Direct partner response within 4 hours.',
};

const inputCls = 'w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder-white/25 focus:outline-none focus:border-white/25 transition';

// Dispatch dossier modal — grey box theme, each card opens it with its own heading/context.
export const DispatchModal = ({ open, onClose, context }) => {
  const [submitted, setSubmitted] = useState(false);
  const c = CONTEXTS[context] || fallback;

  useEffect(() => { if (open) setSubmitted(false); }, [open, context]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-slate-700/75 backdrop-blur-2xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden">
        <button onClick={onClose} className="absolute top-5 right-5 text-white/40 hover:text-white transition z-10" aria-label="Close Modal">
          <X size={20} />
        </button>

        {!submitted ? (
          <div className="p-8 md:p-10">
            <div className="mb-7">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F58220]" />
                <span className="text-xs uppercase tracking-[0.2em] text-white/40 font-medium">{c.badge}</span>
              </div>
              <h3 className="text-2xl font-bold text-white tracking-tight">{c.title}</h3>
              <p className="text-[13px] text-white/45 mt-1.5 leading-relaxed">{c.desc}</p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">Full Name</label>
                  <input type="text" required placeholder="Partner name" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">Institutional Email</label>
                  <input type="email" required placeholder="name@firm.com" className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">Institution / Fund</label>
                  <input type="text" required placeholder="Firm name" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">Dialogue Scope</label>
                  <select
                    className={`${inputCls} appearance-none`}
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23ffffff' viewBox='0 0 24 24'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option className="bg-slate-700">Enterprise Deal OS™ Deployment</option>
                    <option className="bg-slate-700">Live Deal Mandate Sprint</option>
                    <option className="bg-slate-700">Sovereign Enclave & Security</option>
                    <option className="bg-slate-700">Systems & Research Discussion</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">Mandate Scope (Optional)</label>
                <textarea rows="2" placeholder="Brief context on fund, transaction timing, or deployment architecture..." className={`${inputCls} resize-none`} />
              </div>

              <button type="submit" className="w-full flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-[#1e1e1e] font-semibold text-sm rounded-lg px-6 py-3.5 transition-colors mt-1">
                {c.btn} <ArrowRight size={16} />
              </button>
            </form>

            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[10px] text-white/30 uppercase tracking-wider font-medium">
              <span>Bilateral Institutional NDA</span>
              <span>Zero MNPI Retention</span>
            </div>
          </div>
        ) : (
          <div className="p-8 flex flex-col items-center text-center py-16">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-5">
              <CheckCircle2 size={28} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2 tracking-tight">{c.successTitle}</h3>
            <p className="text-sm text-white/45 max-w-xs leading-relaxed">{c.successDesc}</p>
            <button onClick={onClose} className="mt-8 text-sm font-medium text-white/60 hover:text-white transition">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};
